import { css } from '@emotion/react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { OwnershipTimelineStack } from '../../../../schemas/OwnershipTimeline.schema';
import { AgFormatterService } from '../../../../util/ag-formatter-service';
import { colors } from '../../../../theme/colors';

const lotName = css`
  align-items: center;
  display: flex;
  gap: 0.5rem;
  min-width: 0;
`;

const swatch = css`
  border-radius: 2px;
  flex-shrink: 0;
  height: 0.625rem;
  width: 0.625rem;
`;

/**
 * The `showSummaryV2` holdings grid, per design: the lot's own name carrying the chart's series
 * colour, then security type, shares, ownership, cost basis, FMV and the multiple.
 *
 * Two design columns are missing — Fund and Entry Date — because the ownership-timeline response
 * carries neither. See the spec.
 */
export function getHoldingsColDefsV2(
  colorByPosition: Map<string, string>
): ColDef<OwnershipTimelineStack>[] {
  const fmt = AgFormatterService.get();

  return [
    {
      headerName: 'Lot Name',
      field: 'label',
      minWidth: 180,
      cellRenderer: ({ data }: ICellRendererParams<OwnershipTimelineStack>) => {
        if (!data) {
          return null;
        }
        const color = colorByPosition.get(data.position ?? data.label) ?? colors.neutral[50];
        return (
          <div css={lotName}>
            <span css={swatch} style={{ backgroundColor: color }} />
            <span>{data.label}</span>
          </div>
        );
      },
    },
    {
      headerName: 'Security Type',
      field: 'position',
      minWidth: 150,
      // Fall back to the stack label when position is null, mirroring the chart series name.
      valueGetter: (params) => params.data?.position ?? params.data?.label,
    },
    {
      headerName: 'Shares',
      field: 'shares',
      type: 'numericColumn',
      minWidth: 100,
      valueFormatter: fmt.getFormatterForId('integer'),
    },
    {
      headerName: 'Ownership %',
      field: 'ownershipPct',
      type: 'numericColumn',
      minWidth: 120,
      // decimalPercent renders a fraction as a percent (×100), matching the API's ownershipPct.
      valueFormatter: fmt.getFormatterForId('decimalPercent'),
    },
    {
      headerName: 'Cost Basis',
      field: 'cost',
      type: 'numericColumn',
      minWidth: 110,
      valueFormatter: fmt.getFormatterForId('currencyShort'),
    },
    {
      headerName: 'FMV',
      field: 'fmv',
      type: 'numericColumn',
      minWidth: 110,
      valueFormatter: fmt.getFormatterForId('currencyShort'),
    },
    {
      headerName: 'MOIC',
      colId: 'moic',
      type: 'numericColumn',
      minWidth: 90,
      // Derived, not returned by the API: FMV over cost basis, undefined when nothing was invested
      // so the formatter renders a dash rather than a bogus multiple.
      valueGetter: ({ data }) => (!data?.cost ? undefined : data.fmv / data.cost),
      valueFormatter: fmt.getFormatterForId('multiplier'),
    },
  ];
}

export function getHoldingsColDefs(): ColDef<OwnershipTimelineStack>[] {
  const fmt = AgFormatterService.get();
  return [
    {
      headerName: 'Round',
      field: 'roundId',
      minWidth: 100,
      valueFormatter: fmt.getFormatterForId('round'),
    },
    {
      headerName: 'Security',
      field: 'position',
      minWidth: 200,
      // Fall back to the stack label when position is null, mirroring the chart series name.
      valueGetter: (params) => params.data?.position ?? params.data?.label,
    },
    {
      headerName: 'Shares',
      field: 'shares',
      type: 'numericColumn',
      minWidth: 100,
      valueFormatter: fmt.getFormatterForId('integer'),
    },
    {
      headerName: 'Ownership %',
      field: 'ownershipPct',
      type: 'numericColumn',
      minWidth: 120,
      // decimalPercent renders a fraction as a percent (×100), matching the API's ownershipPct.
      valueFormatter: fmt.getFormatterForId('decimalPercent'),
    },
    {
      headerName: 'Cost',
      field: 'cost',
      type: 'numericColumn',
      minWidth: 110,
      valueFormatter: fmt.getFormatterForId('currencyShort'),
    },
    {
      headerName: 'FMV',
      field: 'fmv',
      type: 'numericColumn',
      minWidth: 110,
      valueFormatter: fmt.getFormatterForId('currencyShort'),
    },
  ];
}
